import React, { useState, useEffect } from 'react';
import { Box, Text } from 'ink';
import type { AppCallbacks, ChoiceInputState, ToolCall } from './types';
import { useKeyHandler } from './lib/key_handler';
import { COMMAND_TOOLS, Tool } from './messages/tools/Tool';
import { useTheme } from './lib/theme';

interface ChoiceInputProps {
  input: ChoiceInputState;
  callbacks: AppCallbacks;
  subagentApprovalPending?: boolean;
}

const waitingHeader = (requestedBy: string | undefined, toolCall: ToolCall): string => {
  const subject = COMMAND_TOOLS.has(toolCall.input.tool) ? 'command' : 'tool call';
  return `${requestedBy ?? 'The main agent'} is waiting on this ${subject}`;
};

export const ChoiceInput: React.FC<ChoiceInputProps> = ({
  input,
  callbacks,
  subagentApprovalPending = false,
}) => {
  const { tokens } = useTheme();
  const options = input.choiceOptions || [];
  const [selectedIndex, setSelectedIndex] = useState(input.selectedChoiceIndex ?? 0);

  // Sync with prop changes (when state is updated externally)
  useEffect(() => {
    setSelectedIndex(input.selectedChoiceIndex ?? 0);
  }, [input.selectedChoiceIndex]);

  useKeyHandler((event) => {
    if (event.eventType !== 'press') return;

    if (event.name === 'up') {
      setSelectedIndex((prev) => Math.max(0, prev - 1));
      event.stopPropagation();
      return;
    }

    if (event.name === 'down') {
      setSelectedIndex((prev) => Math.min(options.length - 1, prev + 1));
      event.stopPropagation();
      return;
    }

    if (event.name === 'return') {
      callbacks.onChoiceSubmit(options[selectedIndex]?.value);
      event.stopPropagation();
    }
  });

  const hint = subagentApprovalPending
    ? '↑↓ to choose · enter to confirm · the sub agent is blocked until you answer'
    : `Use ↑/↓ arrows to navigate • Enter to select • Ctrl+C to exit`;

  return (
    <Box flexDirection="column">
      {input.approval && (
        <Box flexDirection="column" marginBottom={1}>
          <Box marginBottom={1}>
            <Text color={tokens.text.warning}>
              ↳ {waitingHeader(input.approval.requestedBy, input.approval.toolCall)}
            </Text>
          </Box>
          <Box paddingLeft={2}>
            <Tool tool={input.approval.toolCall} expanded={false} />
          </Box>
        </Box>
      )}
      <Box flexDirection="column" paddingLeft={input.approval ? 4 : 2}>
        {options.map((option, index) => {
          const isSelected = index === selectedIndex;
          return (
            <Box key={index} flexDirection="column">
              <Box>
                {/* › indicator shifts with selection; two spaces preserve alignment when unselected */}
                <Text
                  color={isSelected ? tokens.text.accent : tokens.text.default}
                  bold={isSelected}
                >
                  {isSelected ? '› ' : '  '}
                  {option.label}
                </Text>
                {option.description && (
                  <Text color={tokens.text.subtle}> - {option.description}</Text>
                )}
              </Box>
              {option.secondaryLabel && (
                <Box paddingLeft={4}>
                  <Text color={isSelected ? tokens.text.accent : undefined} dimColor={!isSelected}>
                    {option.secondaryLabel}
                  </Text>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <Box marginTop={1} paddingLeft={input.approval ? 4 : 2}>
        <Text dimColor>{hint}</Text>
      </Box>
    </Box>
  );
};
